import React, { useCallback, useContext } from 'react';
import { If } from '@/components/Utils/If';
import {
  Pagination,
  resolvePaginationTotal,
} from '@/components/Datatable/Pagination';
import TableContext from './TableContext';
import { saveInvoke } from '@/utils';

/**
 * Table pagination.
 */
export default function TablePagination() {
  const {
    table: {
      gotoPage,
      setPageSize,
      pageCount,
      rows,
      state: { pageIndex, pageSize },
    },
    props: {
      pagination,
      loading,
      onPaginationChange,
      hidePaginationNoPages,
      manualPagination = true,
      // Nombre TOTAL d'enregistrements renvoye par l'API. Quand il est fourni
      // il prime : `pageSize * pageCount` n'est qu'une ESTIMATION arrondie a
      // la page (20 x 3 = 60 pour 55 enregistrements), ce qui faisait mentir
      // le pied de tableau (F45).
      total: controlledTotal,
    },
  } = useContext(TableContext);

  const triggerOnPaginationChange = useCallback(
    (payload: { pageIndex: number; pageSize: number }) => {
      saveInvoke(onPaginationChange, payload);
    },
    [onPaginationChange],
  );

  // Handles the page changing.
  const handlePageChange = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) => {
      const pageIndex = page - 1;
      gotoPage(pageIndex);
      triggerOnPaginationChange({ pageIndex, pageSize });
    },
    [gotoPage, triggerOnPaginationChange],
  );

  // Handles the page size changing.
  const handlePageSizeChange = useCallback(
    ({ pageSize, page }: { pageSize: number; page: number }) => {
      const pageIndex = 0;

      gotoPage(pageIndex);
      setPageSize(pageSize);

      triggerOnPaginationChange({ pageIndex, pageSize });
    },
    [gotoPage, setPageSize, triggerOnPaginationChange],
  );

  // Detarmines when display the pagination.
  const showPagination =
    pagination &&
    ((hidePaginationNoPages && pageCount > 1) || !hidePaginationNoPages) &&
    !loading;

  return (
    showPagination && (
      <Pagination
        currentPage={pageIndex + 1}
        // Un vrai total fourni par la page prime toujours ; sinon on compte les
        // lignes (pagination client) ; l'estimation `pageSize x pageCount` n'est
        // plus qu'un dernier recours. Attention : `Number(null)` vaut 0 — un
        // total absent ne doit donc PAS passer par `Number()` seul, sinon une
        // liste dont le total n'est pas encore chargé annonce « 0 entrée ».
        total={resolvePaginationTotal({
          controlledTotal,
          manualPagination,
          pageSize,
          pageCount,
          rowsLength: rows.length,
        })}
        size={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    )
  );
}
